import React, { useState, useEffect, useMemo } from 'react';
import ListItem from '@material-ui/core/ListItem';
import List from '@material-ui/core/List';
import IconButton from '@material-ui/core/IconButton';
import './style.scss';
import CalendarUtils from 'services/CalendarUtils';
import ProgressMessage from 'components/Utils/ProgressMessage';
import moment from 'moment-timezone';
import DefaultDatePicker from 'components/Utils/DefaultDatePicker';
import DefaultAutoComplete from 'components/Utils/DefaultAutoComplete';
import GenericBadge from 'components/Utils/GenericBadge';
import DefaultButton from 'components/Utils/NewDefaultButton';
import Icon from 'components/Utils/NewIcon';
import DefaultModal from 'components/Utils/DefaultModal';
import PatientBadge from 'components/Patient/PatientBadge';
import { StaffBadge } from 'services/StaffUtils';
import { useAppDispatch } from 'careand-redux/useAppDispatch';
import { useAppSelector } from 'careand-redux/store';
import { api } from 'careand-redux/api/ApiThunks';
import { useIsLoading } from 'careand-redux/slices/ui/hooks';
import { UncontrolledCollapse } from 'components/Utils/Collapse';
import DocumentDetailsModal from 'components/HealthRecords/Documents/DocumentDetailsModal';
import ConfirmationIcon from 'components/Utils/NewIcon/ConfirmationIcon';

const DEFAULT_ARRAY = [];

const Reminders = ({ user, userRole, document }) => {
  const dispatch = useAppDispatch();
  const reminders = useAppSelector((state) => state.reminders[user.id]) || DEFAULT_ARRAY;

  const loadingReminders = useIsLoading(api.Reminders.getReminders);

  useEffect(() => {
    dispatch(api.Reminders.getReminders({ userId: user.id }));
  }, [dispatch, user]);

  const { archived, active } = useMemo(() => {
    const archived = reminders.filter((reminder) => !!reminder.archived);
    const active = reminders.filter((reminder) => !reminder.archived);
    return { archived, active };
  }, [reminders]);

  return (
    <div className="reminders">
      <ProgressMessage show={loadingReminders} topRight={true} />
      {userRole === 'patient' && <PatientBadge patient={user} />}
      {userRole !== 'patient' && <StaffBadge user={user} />}
      <ReminderForm user={user} userRole={userRole} document={document} />
      <UncontrolledCollapse title={`Active Reminders (${active.length})`} initialValue={false}>
        <RemindersList reminders={active} loading={loadingReminders} />
      </UncontrolledCollapse>
      {archived.length > 0 && (
        <UncontrolledCollapse title={`Archived Reminders (${archived.length})`}>
          <RemindersList reminders={archived} loading={loadingReminders} />
        </UncontrolledCollapse>
      )}
    </div>
  );
};

const RemindersList = ({ reminders, loading }) => {
  return (
    <div className="list">
      <List>
        {!loading && reminders.length === 0 && (
          <span key="no-element" className="padded-title">
            No reminders to show
          </span>
        )}
        {reminders.map((reminder) => (
          <ReminderItem key={reminder.id} reminder={reminder} />
        ))}
      </List>
    </div>
  );
};

const ReminderForm = ({ user, userRole, document }) => {
  const dispatch = useAppDispatch();
  const [date, setDate] = useState(moment().unix());
  const [message, setMessage] = useState('');
  const [selectedDocument] = useState(document);
  const [loading, setLoading] = useState(false);

  const hasParentAccounts = user?.linked_to_accounts?.length > 0;

  async function onSubmit() {
    const reminder = {
      user_id: user.id,
      user_role: userRole,
      message,
      date,
      document_id: selectedDocument?.id,
    };
    setLoading(true);
    let result = await dispatch(api.Reminders.addReminder({ userId: user.id, reminder }), {
      errorTitle: 'Add Reminder',
    });
    setLoading(false);
    if (result) {
      setMessage('');
      setDate(moment().unix());
    }
  }

  return (
    <div className="form-wrapper">
      <span className="small-title">Add Reminder</span>
      <div className="reminder-warning">
        <Icon fontAwesome="circle-info" width="16px" height="16px" />
        <span>
          {hasParentAccounts
            ? "Reminder texts should not contain PHI. This is a linked account, so the reminder will be sent to the parent account with the patient's initials."
            : 'Reminder texts should not contain PHI.'}
        </span>
      </div>
      <DefaultDatePicker name="Date" value={date} onChange={(event, date) => setDate(date)} />
      <DefaultAutoComplete
        style={{ width: '100%' }}
        name="message"
        placeholder="Message"
        input={{
          value: message,
          onChange: (value) => setMessage(value),
        }}
        onUpdateInput={(value) => {
          setMessage(value);
        }}
        dataSource={MESSAGES}
      />
      {selectedDocument && (
        <div className="document">
          <Icon
            loading={loading}
            fontAwesome="paperclip"
            showTooltip={true}
            width="15px"
            height="15px"
          />
          <span className="small-title">{selectedDocument.title}</span>
        </div>
      )}
      <DefaultButton name="Submit" loading={loading} disabled={!message} onClick={onSubmit} />
    </div>
  );
};

const ReminderItem = ({ reminder }) => {
  const dispatch = useAppDispatch();
  const [loading, setLoading] = useState(false);
  const [document, setDocument] = useState();
  const [openDocumentModal, setOpenDocumentModal] = useState(false);

  useEffect(() => {
    dispatch(api.Documents.getDocument({ documentId: reminder.document_id })).then((result) => {
      if (result) setDocument(result);
    });
  }, [dispatch, reminder.document_id]);

  async function onDelete() {
    setLoading(true);
    await dispatch(
      api.Reminders.deleteReminder({
        userId: reminder.user_id,
        id: reminder.id,
      }),
    );
    setLoading(false);
  }

  const message = `${reminder.message}`;

  return (
    <ListItem key={reminder.id}>
      {openDocumentModal && document && (
        <DocumentDetailsModal
          document={document}
          onClose={() => setOpenDocumentModal(false)}
          isOpen={!!openDocumentModal}
        />
      )}
      <GenericBadge
        className="reminder-badge"
        title={
          <>
            <span className="wrap-text">{message}</span>
            {!reminder.archived && (
              <ConfirmationIcon
                loading={loading}
                width="20px"
                src="trash"
                alt="Remove Reminder"
                showTooltip={true}
                confirmText="Are you sure you want to delete this reminder?"
                onClick={onDelete}
              />
            )}
          </>
        }
        avatar={
          reminder.document_id && (
            <Icon
              loading={loading}
              alt={document?.title}
              fontAwesome="paperclip"
              showTooltip={true}
              className="white"
              width="25px"
              height="25px"
              onClick={() => setOpenDocumentModal(true)}
            />
          )
        }
        info={`${CalendarUtils.format(reminder.date)}${
          !reminder.archived ? ` - ${CalendarUtils.fromNow(reminder.date)}` : ''
        }`}
      />
    </ListItem>
  );
};

export const RemindersButton = ({
  user,
  userRole,
  width = '30px',
  className = 'blue',
  button = false,
  document,
}) => {
  const [open, setOpen] = useState(false);
  return (
    <>
      {button && (
        <IconButton onClick={() => setOpen(true)}>
          <Icon
            src="hourglass"
            alt="Reminders"
            showTooltip={true}
            height={width}
            width={width}
            className={className}
          />
        </IconButton>
      )}
      {!button && (
        <Icon
          src="hourglass"
          alt="Reminders"
          showTooltip={true}
          height={width}
          width={width}
          className={className}
          onClick={() => setOpen(true)}
        />
      )}
      {open && (
        <DefaultModal
          title="Reminders"
          className="modal reminders-modal animated fadeIn"
          wrap={true}
          padding={true}
          content={<Reminders user={user} userRole={userRole} document={document} />}
          isOpen={open}
          onRequestClose={() => setOpen(false)}
          draggable={true}
        />
      )}
    </>
  );
};

const MESSAGES = [
  'Bone Mineral Density Scan',
  'Colon Cancer Screening Test',
  'Colonoscopy',
  'Pap',
  'HPV',
  'Mammogram',
  'IUD Check',
  'Repeat Imaging',
  'Repeat Bloodwork',
  'Repeat Urine',
  'Suture Removal',
  'Prenatal Follow-up',
  'Cryotherapy',
  'Sexual Health Visit',
];

export default Reminders;
